import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import clsx from 'clsx';
import { Skeleton } from '../Skeletons';
import { Link } from 'react-router';

export interface NavTab {
  title: string;
  href: string;
}

export interface TabNavProps {
  tabs: (NavTab | null)[];
  children?: React.ReactNode;
  loading?: boolean;
}

// Fades match the page background so tabs appear to slide under them
const FADE_CLASSES =
  'pointer-events-none absolute top-0 bottom-0.5 z-20 w-12 transition-opacity duration-200 from-grey-300 dark:from-blue-1100 to-transparent';

/**
 * Tabs stay on one row and scroll sideways when they don't fit (swipe on
 * touch screens). A fade on either edge shows there are more tabs that way.
 */
export const TabNav = ({ tabs, children, loading }: TabNavProps) => {
  const location = useLocation();
  const path = location.pathname;

  const scrollerRef = useRef<HTMLElement>(null);
  const [overflow, setOverflow] = useState({ left: false, right: false });

  const updateOverflow = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    // 1px tolerance for fractional widths
    const left = el.scrollLeft > 1;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setOverflow((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
  }, []);

  // Recheck when the tabs or the available width change
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateOverflow();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(updateOverflow);
    observer.observe(el);
    return () => observer.disconnect();
  }, [updateOverflow, tabs, loading]);

  // Bring the current tab into view, e.g. a tab that starts off the right edge
  useEffect(() => {
    const el = scrollerRef.current;
    const selected = el?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!el || !selected) return;
    const target = selected.offsetLeft - (el.clientWidth - selected.offsetWidth) / 2;
    el.scrollLeft = Math.max(0, target);
    updateOverflow();
  }, [path, loading, updateOverflow]);

  return (
    <>
      {loading ? (
        <Skeleton height="2rem" />
      ) : (
        // Same trick as Table: a single minmax(0, 1fr) grid column caps the tabs at the
        // parent's width, so the row scrolls inside the nav instead of widening the page
        <div className="grid relative mb-14 w-full grid-cols-[minmax(0,1fr)] max-w-[1400px]">
          <nav
            ref={scrollerRef}
            onScroll={updateOverflow}
            className="overflow-x-auto relative min-w-0 no-scrollbar overscroll-x-contain"
          >
            <ul className="flex flex-nowrap gap-y-1 min-w-full border-b-2 w-max dark:border-blue-900 border-grey-400">
              {tabs.map((tab: NavTab | null, i: number) => {
                if (!tab) return;

                const selected = path === tab.href;

                return (
                  <li
                    key={tab.title + i}
                    className={clsx(
                      ' -mb-0.5 flex justify-center rounded-b-none rounded-t-md cursor-pointer transition-colors text-grey-1000 mr-0 min-w-[150px] sm:min-w-[200px] whitespace-nowrap relative z-10',
                      'hover:!bg-blue-200 hover:!border-blue-800 dark:hover:!bg-blue-900 dark:hover:!border-blue-800 text-sm',
                      selected
                        ? 'bg-transparent !border-blue-800 font-bold dark:text-blue-400 text-blue-800'
                        : 'border-transparent text-grey-500 dark:text-grey-400 dark:border-transparent'
                    )}
                  >
                    <Link
                      to={tab.href}
                      key={tab.href}
                      aria-current={selected ? 'page' : undefined}
                      className={clsx(
                        'w-full text-center whitespace-nowrap px-2 py-1.5 block hover:border-b-2 hover:border-blue-800',
                        selected
                          ? 'bg-transparent !border-blue-800 border-b-2 font-bold dark:text-blue-400 text-blue-800'
                          : 'border-transparent  dark:border-transparent'
                      )}
                    >
                      {tab.title}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>
          <div
            aria-hidden
            data-testid="tabnav-fade-left"
            className={clsx(
              FADE_CLASSES,
              'left-0 bg-linear-to-r',
              overflow.left ? 'opacity-100' : 'opacity-0'
            )}
          />
          <div
            aria-hidden
            data-testid="tabnav-fade-right"
            className={clsx(
              FADE_CLASSES,
              'right-0 bg-linear-to-l',
              overflow.right ? 'opacity-100' : 'opacity-0'
            )}
          />
        </div>
      )}
      {children}
    </>
  );
};
